/**
 * Phase 11 Beat 3 — Portal Timeline (chronological candidate lifecycle).
 * Reads from GET /api/portal/timeline.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  User, CheckCircle, XCircle, ChatCentered, Trophy, PaperPlaneTilt,
  Sparkle, ClockCounterClockwise, PencilSimple, UploadSimple,
} from "@phosphor-icons/react";

const ICONS = {
  dossier_saved: PencilSimple,
  dossier_submitted: PaperPlaneTilt,
  evaluation_started: Trophy,
  evaluation_completed: Trophy,
  evaluation_created: Trophy,
  evaluation_imported: UploadSimple,
  change_request_submitted: PaperPlaneTilt,
  change_request_approved: CheckCircle,
  change_request_rejected: XCircle,
  change_request_clarification: ChatCentered,
  change_request_resubmitted: PaperPlaneTilt,
  profile_updated: Sparkle,
};

const TONE = {
  dossier_saved: "slate", dossier_submitted: "sky",
  evaluation_started: "amber", evaluation_completed: "emerald",
  evaluation_created: "amber", evaluation_imported: "indigo",
  change_request_submitted: "sky", change_request_approved: "emerald",
  change_request_rejected: "red", change_request_clarification: "amber",
  change_request_resubmitted: "sky", profile_updated: "emerald",
};

const LABELS = {
  dossier_saved: "Dossier saved",
  dossier_submitted: "Dossier submitted",
  evaluation_started: "Evaluation started",
  evaluation_completed: "Evaluation completed",
  evaluation_created: "Evaluation created",
  evaluation_imported: "Evaluation imported (migration)",
  change_request_submitted: "Change request submitted",
  change_request_approved: "Change request approved",
  change_request_rejected: "Change request rejected",
  change_request_clarification: "Clarification requested",
  change_request_resubmitted: "Change request resubmitted",
  profile_updated: "Profile updated",
};

export default function PortalTimeline({ authFetch }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await authFetch("/api/portal/timeline");
      const d = await r.json();
      setRows(d.rows || []);
    } catch (e) { toast.error(e.message); setRows([]); }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  if (rows === null) return <div className="text-slate-400 text-sm">Loading timeline…</div>;

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500" data-testid="portal-timeline-empty">
        <ClockCounterClockwise size={32} className="mx-auto mb-2 text-slate-300" />
        <div>No timeline entries yet.</div>
        <div className="text-xs mt-1">As you save your dossier, submit for evaluation, and request changes, they'll appear here.</div>
      </div>
    );
  }

  return (
    <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4" data-testid="portal-timeline">
      {rows.map((t, i) => {
        const Icon = ICONS[t.kind] || User;
        const tone = TONE[t.kind] || "slate";
        const toneMap = {
          slate: "bg-slate-100 text-slate-600 border-slate-200",
          sky: "bg-sky-100 text-sky-700 border-sky-200",
          emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
          red: "bg-red-100 text-red-700 border-red-200",
          amber: "bg-amber-100 text-amber-700 border-amber-200",
          indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
        };
        return (
          <li key={`tl-${t.at || ""}-${t.kind}-${i}`} className="ml-6" data-testid={`portal-timeline-${t.kind}`}>
            <span className={`absolute -left-[13px] mt-1 w-6 h-6 rounded-full border-2 grid place-items-center ${toneMap[tone]}`}>
              <Icon size={12} weight="fill" />
            </span>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-900">{LABELS[t.kind] || t.kind}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{(t.at || "").slice(0, 10)}</div>
              </div>
              {t.meta && Object.keys(t.meta).length > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  {Object.entries(t.meta).slice(0, 4).map(([k, v]) => (
                    <span key={k} className="mr-3">
                      <span className="text-slate-400">{k}:</span>{" "}
                      <span className="text-slate-700">
                        {typeof v === "boolean" ? (v ? "yes" : "no")
                          : Array.isArray(v) ? v.slice(0, 3).join(", ")
                          : typeof v === "string" && v.length > 40 ? v.slice(0, 40) + "…"
                          : String(v ?? "")}
                      </span>
                    </span>
                  ))}
                </div>
              )}
              {t.by?.name && (
                <div className="text-xs text-slate-400 mt-1">by {t.by.name}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
